import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const VehicleSubstitutionsTab = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [searchText, setSearchText] = useState('');
  const [preset, setPreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const showNotif = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('vehicle_substitution_requests')
        ?.select('*, buyer_bookings(factory_name, grade, quantity)')
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error('VehicleSubstitutionsTab error:', err?.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    const channel = supabase?.channel('vsr_approvals_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_substitution_requests' }, () => loadRequests())
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadRequests]);

  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredRequests = requests.filter(req => {
    const text = searchText.toLowerCase();
    if (text && ![
      req?.readable_booking_id,
      req?.old_vehicle_number,
      req?.new_vehicle_number,
      req?.buyer_bookings?.factory_name,
    ].some(f => (f || '').toLowerCase().includes(text))) return false;
    if (dateFrom && new Date(req?.created_at) < dateFrom) return false;
    if (dateTo && new Date(req?.created_at) > dateTo) return false;
    return true;
  });

  const handleApprove = async (req) => {
    setProcessing(p => ({ ...p, [req?.id]: 'approving' }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const approvedBy = session?.user?.email || user?.email;

      const { error } = await supabase.functions.invoke('process-vehicle-substitution', {
        body: { request_id: req?.id, action: 'approve', approved_by: approvedBy },
      });

      if (error) throw error;
      showNotif(`Approved. VAHAN check running for ${req?.readable_booking_id}.`);
      loadRequests();
    } catch (err) {
      showNotif(`Failed: ${err?.message}`, 'error');
    } finally {
      setProcessing(p => { const n = { ...p }; delete n[req?.id]; return n; });
    }
  };

  const handleReject = async (req) => {
    setProcessing(p => ({ ...p, [req?.id]: 'rejecting' }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const approvedBy = session?.user?.email || user?.email;

      const { error } = await supabase.functions.invoke('process-vehicle-substitution', {
        body: { request_id: req?.id, action: 'reject', approved_by: approvedBy, notes: rejectNotes },
      });

      if (error) throw error;
      showNotif(`Request rejected for ${req?.readable_booking_id}.`);
      setRejectTarget(null);
      setRejectNotes('');
      loadRequests();
    } catch (err) {
      showNotif(`Failed: ${err?.message}`, 'error');
    } finally {
      setProcessing(p => { const n = { ...p }; delete n[req?.id]; return n; });
    }
  };

  const reasonLabel = {
    breakdown: '🔧 Breakdown',
    no_show: '🚫 No Show',
    other: '📝 Other',
  };

  return (
    <div className="space-y-5">
      {/* Notification */}
      {notification?.show && (
        <div
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{
            background: notification?.type === 'error' ? '#dc2626' : '#1e4a72',
            border: `1px solid ${notification?.type === 'error' ? '#b91c1c' : '#2d6aa0'}`,
          }}
        >
          <Icon name={notification?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={18} color="#fff" />
          {notification?.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Vehicle Change Requests</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading
              ? 'Loading...'
              : `${filteredRequests?.length} pending request${filteredRequests?.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={loadRequests}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        >
          <Icon name="RefreshCw" size={13} />
          Refresh
        </button>
      </div>

      {/* Search & Date Filter */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search by Booking ID, vehicle number, factory..."
          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <DateRangeFilter
          preset={preset} customFrom={customFrom} customTo={customTo}
          onPresetChange={setPreset} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading requests...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && filteredRequests?.length === 0 && (
        <div className="text-center py-16 rounded-xl bg-gray-50 border border-gray-100">
          <Icon name="CheckCircle" size={40} color="#86efac" />
          <p className="mt-3 text-sm font-medium text-gray-400">
            {requests?.length === 0 ? 'No pending vehicle change requests' : 'No requests match your search'}
          </p>
          <p className="text-xs mt-1 text-gray-300">
            {requests?.length === 0 ? 'All clear — nothing awaiting approval' : 'Try adjusting your search or date filter'}
          </p>
        </div>
      )}

      {/* Request Cards */}
      {!loading && filteredRequests?.length > 0 && (
        <div className="space-y-4">
          {filteredRequests?.map(req => {
            const isProcessing = !!processing?.[req?.id];
            const isRejectOpen = rejectTarget === req?.id;
            const booking = req?.buyer_bookings;

            return (
              <div key={req?.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                {/* Top row */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono font-bold text-sm text-gray-900">
                        {req?.readable_booking_id}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {reasonLabel?.[req?.reason] || req?.reason}
                      </span>
                    </div>
                    {booking && (
                      <p className="text-xs text-gray-500">
                        {booking?.factory_name} · {booking?.grade} · {booking?.quantity} MT
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      Requested {new Date(req?.created_at)?.toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap self-start">
                    🕐 Pending Approval
                  </span>
                </div>

                {/* Vehicle change */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 mb-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Current Vehicle</p>
                    <p className="font-mono font-bold text-sm text-red-600 truncate">
                      {req?.old_vehicle_number}
                    </p>
                  </div>
                  <Icon name="ArrowRight" size={16} color="#9ca3af" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Requested Vehicle</p>
                    <p className="font-mono font-bold text-sm text-green-600 truncate">
                      {req?.new_vehicle_number}
                    </p>
                  </div>
                </div>

                {/* Reject notes input */}
                {isRejectOpen && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-gray-600 block mb-1">
                      Rejection Reason (optional)
                    </label>
                    <textarea
                      value={rejectNotes}
                      onChange={e => setRejectNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g. Vehicle registration expired..."
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                  {!isRejectOpen ? (
                    <>
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {processing?.[req?.id] === 'approving' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <Icon name="CheckCircle" size={15} />
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(req?.id); setRejectNotes(''); }}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors disabled:opacity-60"
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleReject(req)}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {processing?.[req?.id] === 'rejecting' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Rejecting...
                          </>
                        ) : (
                          'Confirm Reject'
                        )}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(null); setRejectNotes(''); }}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VehicleSubstitutionsTab;
